import { createSelector } from "@reduxjs/toolkit";
import customSort from "../../../../../../helpers/sortByProp";
import { selectSearchState } from "../../selectors/shared";

const REFINEMENT_SEPARATOR = "|";
const PRICE_SEPARATOR = "<";
const ID_VALUES_SEPARATOR = ":";
const emptyObject = {};
const emptyArray = [];

const getQuery = (state) => selectSearchState(state).query ?? emptyObject;
const getRefine = (state) => getQuery(state).refine ?? "";
const getSortFromQuery = (state) => getQuery(state).sort;
const getSearchFacets = (state) =>
  selectSearchState(state).facets ?? emptyArray;

const alphabetically = customSort((refinement) => refinement.refinementId);

const getPriceMinMax = (refinementValues) => {
  const [min, max] = refinementValues.split(PRICE_SEPARATOR);

  return {
    min: Number(min),
    max: Number(max),
  };
};

const getRefinementFromQuery = (query) => {
  const [refinementId, refinementValues] = query.split(ID_VALUES_SEPARATOR);

  return {
    refinementId,
    refinementValues,
  };
};

const getParsedRefinements = createSelector([getRefine], (refine) => {
  if (!refine) {
    return emptyArray;
  }

  return refine
    .split(REFINEMENT_SEPARATOR)
    .map(getRefinementFromQuery)
    .filter(({ refinementId }) => Boolean(refinementId))
    .sort(alphabetically);
});

const getFacetLookup = createSelector([getSearchFacets], (facets) =>
  facets.reduce((lookup, facet) => {
    lookup[facet.id] = {
      refinementName: facet.name,
      selections:
        facet.facetValues
          ?.filter(({ isSelected }) => isSelected)
          .map(({ id, name }) => ({
            selectionId: id,
            selectionName: name,
          })) ?? emptyArray,
    };

    return lookup;
  }, {}),
);

const buildRefinement = (facetLookup, { refinementId, refinementValues }) => {
  const matchingFacet = facetLookup[refinementId] ?? emptyObject;
  const defaultRefinement = {
    refinementId,
    ...(matchingFacet.refinementName !== undefined
      ? { refinementName: matchingFacet.refinementName }
      : emptyObject),
  };

  if (refinementId === "currentprice") {
    return {
      ...defaultRefinement,
      ...getPriceMinMax(refinementValues),
    };
  }

  return {
    ...defaultRefinement,
    selections: matchingFacet.selections ?? emptyArray,
  };
};

const buildSort = (sort) => [
  {
    refinementId: "sort",
    refinementName: "sort",
    selections: [{ selectionId: sort }],
  },
];

const getAllAppliedRefinements = createSelector(
  [getParsedRefinements, getSortFromQuery, getFacetLookup],
  (parsedRefinements, sort, facetLookup) => {
    if (!parsedRefinements.length && !sort) {
      return emptyArray;
    }

    const refinements = parsedRefinements.map((refinement) =>
      buildRefinement(facetLookup, refinement),
    );

    return sort ? refinements.concat(buildSort(sort)) : refinements;
  },
);

export { getAllAppliedRefinements as default, getQuery };
